import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Loader2, Check, CheckCheck, Play, Pause, User,
  MoreVertical, Pencil, Trash2, Smile
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API}${path}`;
};

/**
 * DM message bubble — matches the Coach conversation style:
 * bordered "card" with a header row (avatar + name + time) and the
 * message content below. Sender ("You") gets primary/yellow tint,
 * receiver (buddy) gets sky/blue tint so the two are instantly
 * distinguishable in the thread.
 */
export const MessageBubble = ({
  msg,
  isFromMe,
  currentUserId,
  currentUserName,
  otherUser,
  onEdit,
  onDelete,
  onAddReaction,
  onRemoveReaction,
  toggleAudioPlayback,
  playingAudioId,
  formatTime,
  formatDuration,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [deleting, setDeleting] = useState(false);

  const msgType = msg.message_type || 'text';

  const getUserReaction = (reactions) => {
    if (!reactions) return null;
    for (const [emoji, userIds] of Object.entries(reactions)) {
      if (userIds.includes(currentUserId)) return emoji;
    }
    return null;
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditContent(msg.content);
    setShowMenu(false);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    await onEdit(msg.id, editContent.trim());
    setIsEditing(false);
    setEditContent('');
  };

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(msg.id);
    setDeleting(false);
    setShowMenu(false);
  };

  // Palette matches the coach conversation:
  //   own → primary/yellow card, buddy → sky/blue card.
  const palette = isFromMe
    ? { bg: 'bg-primary/15', border: 'border-primary/50', name: 'text-primary', avatarBg: 'bg-primary/20', avatarText: 'text-primary' }
    : { bg: 'bg-sky-500/15', border: 'border-sky-400/40', name: 'text-sky-300', avatarBg: 'bg-sky-500/20', avatarText: 'text-sky-300' };

  const displayName = isFromMe ? 'You' : (otherUser?.name || 'Buddy');
  const avatarUrl = isFromMe ? null : getImageUrl(otherUser?.profile_picture);
  const avatarInitial = (displayName || 'U').charAt(0).toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-3 group`}
    >
      <div className={`max-w-[80%] relative ${isFromMe ? 'order-2' : 'order-1'}`}>
        {/* Actions menu (own messages only) */}
        {isFromMe && !isEditing && (
          <div className="absolute top-1 -left-8 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 hover:bg-white/10 rounded"
              data-testid={`message-menu-${msg.id}`}
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </button>
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute top-6 right-0 bg-card border border-border shadow-lg z-50 min-w-[120px] rounded-lg overflow-hidden"
                >
                  {msgType === 'text' && (
                    <button onClick={handleStartEdit} className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2" data-testid={`edit-message-${msg.id}`}>
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  )}
                  <button onClick={handleDelete} disabled={deleting} className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2" data-testid={`delete-message-${msg.id}`}>
                    {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Unsend
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Card bubble */}
        <div className={`${palette.bg} ${palette.border} border rounded-2xl p-3 sm:p-4`}>
          {/* Header row */}
          <div className="flex items-center gap-2 mb-2">
            <Avatar className={`h-6 w-6 flex-shrink-0 ring-1 ring-white/10`}>
              {avatarUrl && <AvatarImage src={avatarUrl} />}
              <AvatarFallback className={`${palette.avatarBg} ${palette.avatarText} text-[10px] font-semibold`}>
                {isFromMe && !currentUserName ? <User className="h-3 w-3" /> : avatarInitial}
              </AvatarFallback>
            </Avatar>
            <span className={`text-sm font-semibold ${palette.name}`}>
              {displayName}
            </span>
            <span className="text-muted-foreground text-xs ml-auto whitespace-nowrap">
              {formatTime(msg.created_at)}
              {msg.is_edited && <span className="ml-1 italic">(edited)</span>}
            </span>
            {isFromMe && (
              <span className="ml-1">
                {msg.is_read
                  ? <CheckCheck className="h-3.5 w-3.5 text-primary" />
                  : <Check className="h-3.5 w-3.5 text-muted-foreground" />}
              </span>
            )}
          </div>

          {/* Content */}
          {msgType === 'text' && (
            isEditing ? (
              <div className="space-y-2">
                <Input
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="bg-black/40 border-white/15 text-white placeholder:text-white/40"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                    if (e.key === 'Escape') { setIsEditing(false); setEditContent(''); }
                  }}
                  data-testid={`edit-input-${msg.id}`}
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditContent(''); }} className="h-7 text-xs text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
                  <Button size="sm" onClick={handleSaveEdit} className="h-7 text-xs bg-primary text-black hover:bg-primary/90" data-testid={`save-edit-${msg.id}`}>Save</Button>
                </div>
              </div>
            ) : (
              <p className="text-white whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
            )
          )}

          {msgType === 'photo' && msg.media_url && (
            <div className="max-w-xs">
              <img
                src={`${API}${msg.media_url}`}
                alt="Attachment"
                className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(`${API}${msg.media_url}`, '_blank')}
              />
            </div>
          )}

          {msgType === 'voice' && msg.media_url && (
            <div className="flex items-center gap-3 min-w-[180px]">
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 text-white"
                onClick={() => toggleAudioPlayback(msg.id, msg.media_url)}
              >
                {playingAudioId === msg.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <div className="flex-1">
                <div className="h-1 rounded-full bg-white/15">
                  <div
                    className={`h-full rounded-full ${isFromMe ? 'bg-primary' : 'bg-sky-400'}`}
                    style={{ width: playingAudioId === msg.id ? '50%' : '0%' }}
                  />
                </div>
              </div>
              <span className="text-xs text-white/70">
                {msg.duration ? formatDuration(msg.duration) : '0:00'}
              </span>
            </div>
          )}
        </div>

        {/* Reactions */}
        <div className={`flex items-center gap-2 mt-1.5 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
          {msg.reactions && Object.keys(msg.reactions).length > 0 && (
            <div className="flex gap-1">
              {Object.entries(msg.reactions).map(([emoji, userIds]) => (
                <button
                  key={emoji}
                  onClick={(e) => {
                    e.stopPropagation();
                    userIds.includes(currentUserId) ? onRemoveReaction(msg.id) : onAddReaction(msg.id, emoji);
                  }}
                  className={`text-sm px-1.5 py-0.5 rounded-full border transition-colors ${
                    userIds.includes(currentUserId) ? 'bg-primary/20 border-primary' : 'bg-card border-border hover:border-primary/50'
                  }`}
                  data-testid={`reaction-${msg.id}-${emoji}`}
                >
                  {emoji} {userIds.length > 1 && <span className="text-xs">{userIds.length}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowReactionPicker(!showReactionPicker); }}
              className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded ${showReactionPicker ? 'opacity-100' : ''}`}
              data-testid={`add-reaction-${msg.id}`}
            >
              <Smile className="h-4 w-4 text-muted-foreground hover:text-primary" />
            </button>
            <AnimatePresence>
              {showReactionPicker && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`absolute ${isFromMe ? 'right-0' : 'left-0'} bottom-8 bg-card border border-border shadow-lg z-50 p-2 flex gap-1 rounded-full`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => { onAddReaction(msg.id, emoji); setShowReactionPicker(false); }}
                      className={`text-xl p-1 hover:bg-white/10 rounded-full transition-transform hover:scale-125 ${getUserReaction(msg.reactions) === emoji ? 'bg-primary/20' : ''}`}
                      data-testid={`emoji-${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
